import { Button as AntButton, Tooltip } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import { colors, rgbaFromHex } from '@utils/colorSystem';

/** Primary CTA fill + hover (tenant brand). */
const BUTTON_BRAND_PURPLE = '#7543DC';

/** Shown when `permissionDenied` is true — RBAC guard for action buttons. */
export const DEFAULT_PERMISSION_DENIED_TOOLTIP = "You don't have permission to perform this action.";

const VALID_ANTD_BUTTON_TYPES = new Set(['primary', 'default', 'dashed', 'link', 'text']);
const NATIVE_BUTTON_TYPES = new Set(['button', 'submit', 'reset']);

const isDarkShell = (theme) => theme?.colors?.background?.default === '#141414';

/**
 * App button. Supports optional RBAC: set `permissionDenied` to show disabled state, lock icon, and permission tooltip.
 * For other disabled reasons, use `disabled` + `disabledTooltip` (instant tooltip).
 *
 * Defaults: 40px min height, 14px / 700, 8px icon–label gap, padding 10×16, radius 8px; primary fill uses brand purple #7543DC.
 */
export const Button = ({
    children,
    permissionDenied = false,
    /** When set with `disabled`, shows the same lock suffix as RBAC — use with `disabledTooltip` for non-permission empty states. */
    lockWhenDisabled = false,
    disabledTooltip,
    disabled: disabledProp,
    $gap = '8px',
    /** Font size (label + icon scales with `em` in `.anticon`). */
    $size = '14px',
    $padding = '10px 16px',
    $minHeight,
    $primary = true,
    $blackOnLight = false,
    /** Outline-danger button styling (keeps red on hover). */
    $dangerOutline = false,
    $hoverShadow = false,
    $hoverAnimation = true,
    /** When false, skips the framer-motion entrance wrapper (static render). */
    $entryAnimation = true,
    block,
    icon,
    isHidden,
    type: typeProp,
    htmlType: htmlTypeProp,
    ...props
}) => {
    if (isHidden || permissionDenied) return null;

    const isDisabled = !!disabledProp || permissionDenied;
    const showLockSuffix = permissionDenied || (lockWhenDisabled && isDisabled);
    const tooltipTitle = permissionDenied
        ? DEFAULT_PERMISSION_DENIED_TOOLTIP
        : isDisabled && disabledTooltip
          ? disabledTooltip
          : undefined;

    /** Map native `htmlType` values away from Ant's visual `type`; use Ant `primary` for branded CTAs so icon tokens stay solid white. */
    const antdVisualType =
        typeProp && VALID_ANTD_BUTTON_TYPES.has(typeProp)
            ? typeProp
            : ($primary || $blackOnLight) && !$dangerOutline
              ? 'primary'
              : undefined;
    const resolvedHtmlType =
        htmlTypeProp ?? (typeProp && NATIVE_BUTTON_TYPES.has(typeProp) ? typeProp : undefined) ?? 'button';

    const inner = (
        <StyledButton
            $size={$size}
            $gap={$gap}
            $padding={$padding}
            $minHeight={$minHeight}
            $primary={$primary}
            $isHidden={isHidden}
            $blackOnLight={$blackOnLight}
            $dangerOutline={$dangerOutline}
            $hoverShadow={$hoverShadow}
            $hoverAnimation={$hoverAnimation}
            block={block}
            icon={icon}
            disabled={isDisabled}
            {...(antdVisualType ? { type: antdVisualType } : {})}
            htmlType={resolvedHtmlType}
            {...props}
        >
            {children}
            {showLockSuffix ? <LockOutlined aria-hidden style={{ fontSize: '0.95em' }} /> : null}
        </StyledButton>
    );

    const wrapperStyle = { display: block ? 'block' : 'inline-block', width: block ? '100%' : undefined };

    const withMotion = $entryAnimation ? (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            style={wrapperStyle}
        >
            {inner}
        </motion.div>
    ) : (
        <span style={wrapperStyle}>{inner}</span>
    );

    return tooltipTitle ? (
        <Tooltip title={tooltipTitle} mouseEnterDelay={0} mouseLeaveDelay={0} placement="top">
            <span style={{ display: 'inline-block', lineHeight: 0 }}>{withMotion}</span>
        </Tooltip>
    ) : (
        withMotion
    );
};

const c = colors;

export const StyledButton = styled(AntButton)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    min-height: ${({ $minHeight }) => ($minHeight ? `${$minHeight} !important` : '40px')};
    gap: ${({ $gap }) => ($gap ? `${$gap} !important` : '8px !important')};
    padding: ${({ $padding }) => ($padding ? `${$padding} !important` : '10px 16px !important')};
    border-radius: 8px !important;
    font-size: ${({ $size }) => ($size ? `${$size} !important` : '14px !important')};
    font-weight: 700 !important;
    font-family: var(--font-family-geist), system-ui, sans-serif;
    line-height: 1.25;

    .ant-btn-icon,
    .ant-btn-icon .anticon,
    .ant-btn-loading-icon,
    .ant-btn-loading-icon .anticon,
    .anticon {
        font-size: 1em;
        color: inherit !important;
    }

    /* Primary / on-primary: force label + @ant-design/icons to same white (beats antd .ant-btn-primary icon tint). */
    ${({ $primary, $blackOnLight, $dangerOutline }) =>
        !$dangerOutline && ($primary || $blackOnLight)
            ? css`
                  color: ${c.neutral[0]} !important;

                  && .ant-btn-icon,
                  && .ant-btn-icon .anticon,
                  && .ant-btn-loading-icon .anticon,
                  && span.anticon {
                      color: ${c.neutral[0]} !important;
                  }

                  && .ant-btn-icon svg,
                  && span.anticon svg {
                      color: ${c.neutral[0]} !important;
                  }

                  && .ant-btn-icon {
                      opacity: 1 !important;
                  }

                  && .ant-btn-icon svg path,
                  && span.anticon svg path {
                      fill: currentColor !important;
                  }

                  && .ant-btn-icon svg path[fill='none'],
                  && span.anticon svg path[fill='none'] {
                      fill: none !important;
                      stroke: currentColor !important;
                  }

                  && .ant-btn-icon svg line,
                  && span.anticon svg line {
                      fill: none !important;
                      stroke: currentColor !important;
                  }

                  && .ant-btn-icon svg circle,
                  && span.anticon svg circle {
                      fill: currentColor !important;
                  }
              `
            : ''}

    @media (max-width: 1024px) {
        font-size: ${({ $size }) => ($size ? `calc(${$size} * 0.95) !important` : '13px !important')};
        padding: ${({ $padding }) => {
            if (!$padding) return '9px 14px !important';
            const parts = String($padding).trim().split(/\s+/);
            const v = parts[0] || '10px';
            const h = parts[1] || parts[0] || '16px';
            return `calc(${v} * 0.95) calc(${h} * 0.95) !important`;
        }};
        gap: ${({ $gap }) => ($gap ? `calc(${$gap} * 0.95) !important` : '8px !important')};
    }

    @media (max-width: 768px) {
        font-size: ${({ $size }) => ($size ? `calc(${$size} * 0.93) !important` : '13px !important')};
        padding: ${({ $padding }) => {
            if (!$padding) return '9px 14px !important';
            const parts = String($padding).trim().split(/\s+/);
            const v = parts[0] || '10px';
            const h = parts[1] || parts[0] || '16px';
            return `calc(${v} * 0.9) calc(${h} * 0.9) !important`;
        }};
        gap: ${({ $gap }) => ($gap ? `calc(${$gap} * 0.93) !important` : '8px !important')};
    }

    @media (max-width: 480px) {
        font-size: ${({ $size }) => ($size ? `calc(${$size} * 0.9) !important` : '12px !important')};
        padding: ${({ $padding }) => {
            if (!$padding) return '8px 12px !important';
            const parts = String($padding).trim().split(/\s+/);
            const v = parts[0] || '10px';
            const h = parts[1] || parts[0] || '16px';
            return `calc(${v} * 0.85) calc(${h} * 0.85) !important`;
        }};
        gap: ${({ $gap }) => ($gap ? `calc(${$gap} * 0.9) !important` : '8px !important')};
    }

    ${({ $primary, $blackOnLight }) =>
        $primary && !$blackOnLight
            ? css`
                  background-color: ${BUTTON_BRAND_PURPLE} !important;
                  color: ${c.neutral[0]} !important;
                  border: 1px solid ${BUTTON_BRAND_PURPLE} !important;
              `
            : ''}

    ${({ $primary, $blackOnLight, theme, type }) => {
        if ($primary && !$blackOnLight && type !== 'text') return '';
        if ($blackOnLight) {
            const dark = isDarkShell(theme);
            return css`
                background-color: ${dark ? theme.colors.background.primary || BUTTON_BRAND_PURPLE : BUTTON_BRAND_PURPLE} !important;
                color: ${c.neutral[0]} !important;
                border: 1px solid ${dark ? theme.colors.border.primary || BUTTON_BRAND_PURPLE : BUTTON_BRAND_PURPLE} !important;
            `;
        }
        const dark = isDarkShell(theme);
        if (type === 'text') {
            return css`
                background-color: transparent !important;
                color: ${dark ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.85)'} !important;
                border: 1px solid transparent !important;
            `;
        }
        return css`
            background-color: ${dark ? '#2C2C2C' : c.neutral[0]} !important;
            color: ${dark ? c.neutral[0] : c.neutral[900]} !important;
            border: 1px solid ${dark ? 'transparent' : c.neutral[200]} !important;
        `;
    }}

    transition: ${({ $hoverAnimation }) =>
        $hoverAnimation
            ? 'box-shadow 0.25s ease, background-color 0.25s ease, color 0.25s ease, border-color 0.25s ease'
            : 'none'};
    box-shadow: none;

    &:disabled,
    &.ant-btn-disabled {
        cursor: not-allowed !important;
        pointer-events: auto;
        opacity: 1 !important;

        box-shadow: none !important;
        background-color: ${({ $primary, $blackOnLight, theme }) => {
            if ($blackOnLight) return isDarkShell(theme) ? 'rgba(255, 255, 255, 0.08)' : c.neutral[200];
            if ($primary) return isDarkShell(theme) ? 'rgba(255, 255, 255, 0.08)' : c.neutral[200];
            return isDarkShell(theme) ? '#1f1f1f' : c.neutral[100];
        }} !important;
        color: ${({ theme }) => (isDarkShell(theme) ? 'rgba(255, 255, 255, 0.35)' : c.neutral[500])} !important;
        border-color: ${({ theme }) =>
            isDarkShell(theme) ? 'rgba(255, 255, 255, 0.12)' : c.neutral[200]} !important;

        .ant-btn-icon,
        .ant-btn-icon .anticon,
        .ant-btn-loading-icon .anticon,
        .anticon {
            color: inherit !important;
        }

        && .ant-btn-icon svg path,
        && span.anticon svg path {
            fill: currentColor !important;
            stroke: currentColor !important;
        }
    }

    &:disabled:hover,
    &.ant-btn-disabled:hover {
        background-color: ${({ $primary, $blackOnLight, theme }) => {
            if ($blackOnLight) return isDarkShell(theme) ? 'rgba(255, 255, 255, 0.08)' : c.neutral[200];
            if ($primary) return isDarkShell(theme) ? 'rgba(255, 255, 255, 0.08)' : c.neutral[200];
            return isDarkShell(theme) ? '#1f1f1f' : c.neutral[100];
        }} !important;
        color: ${({ theme }) => (isDarkShell(theme) ? 'rgba(255, 255, 255, 0.35)' : c.neutral[500])} !important;
        border-color: ${({ theme }) =>
            isDarkShell(theme) ? 'rgba(255, 255, 255, 0.12)' : c.neutral[200]} !important;
        box-shadow: none !important;

        .ant-btn-icon,
        .ant-btn-icon .anticon,
        .ant-btn-loading-icon .anticon,
        .anticon {
            color: inherit !important;
        }

        && .ant-btn-icon svg path,
        && span.anticon svg path {
            fill: currentColor !important;
            stroke: currentColor !important;
        }
    }

    &:hover:not(:disabled):not(.ant-btn-disabled) {
        ${({ $primary, $blackOnLight, theme, type }) => {
            if ($primary && !$blackOnLight && type !== 'text') {
                return css`
                    background-color: ${BUTTON_BRAND_PURPLE} !important;
                    color: ${c.neutral[0]} !important;
                    border-color: ${BUTTON_BRAND_PURPLE} !important;
                `;
            }
            if ($blackOnLight) {
                const dark = isDarkShell(theme);
                return css`
                    background-color: ${dark ? theme.colors.background.primary || BUTTON_BRAND_PURPLE : BUTTON_BRAND_PURPLE} !important;
                    color: ${c.neutral[0]} !important;
                    border: 1px solid
                        ${dark ? theme.colors.border.primary || BUTTON_BRAND_PURPLE : BUTTON_BRAND_PURPLE} !important;
                `;
            }
            const dark = isDarkShell(theme);
            if (type === 'text') {
                return css`
                    background-color: ${dark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'} !important;
                    color: ${dark ? 'rgba(255, 255, 255, 0.88)' : 'rgba(0, 0, 0, 0.88)'} !important;
                    border-color: transparent !important;
                `;
            }
            return css`
                background-color: ${dark ? 'rgba(117, 67, 220, 0.15)' : '#f9f6ff'} !important;
                color: ${dark ? '#a78bfa' : BUTTON_BRAND_PURPLE} !important;
                border: 1px solid ${dark ? '#a78bfa' : BUTTON_BRAND_PURPLE} !important;
            `;
        }}
        box-shadow: none !important;
    }

    &:active:not(:disabled):not(.ant-btn-disabled) {
        box-shadow: none !important;
    }

    ${({ $dangerOutline, theme }) =>
        $dangerOutline
            ? css`
                  background: ${isDarkShell(theme)
                      ? rgbaFromHex(c.error[600], 0.12)
                      : rgbaFromHex(c.error[500], 0.08)} !important;
                  color: ${c.error[600]} !important;
                  border: 1px solid ${rgbaFromHex(c.error[600], 0.55)} !important;
                  box-shadow: none !important;

                  .ant-btn-icon,
                  .ant-btn-icon .anticon,
                  .ant-btn-loading-icon .anticon,
                  .anticon {
                      color: inherit !important;
                  }

                  &:hover:not(:disabled):not(.ant-btn-disabled) {
                      background: ${isDarkShell(theme)
                          ? rgbaFromHex(c.error[600], 0.18)
                          : rgbaFromHex(c.error[500], 0.12)} !important;
                      color: ${c.error[700]} !important;
                      border: 1px solid ${rgbaFromHex(c.error[600], 0.75)} !important;
                      box-shadow: none !important;
                  }
              `
            : ''}
    ${({ $isHidden }) =>
        $isHidden &&
        css`
            display: none;
        `}
`;
